/**
 * SEOAudit — full site audit panel.
 * Shows per-product scores, grade breakdown, and per-product generate buttons.
 */
import React, { useState, useEffect } from "react";
import ScoreGauge from "./ScoreGauge";

const GRADE_BADGE = { "A+": "seo-badge-aplus", A: "seo-badge-a", B: "seo-badge-b", C: "seo-badge-c", D: "seo-badge-d", F: "seo-badge-f" };

function gradeFromScore(s) {
  if (s >= 95) return "A+";
  if (s >= 80) return "A";
  if (s >= 65) return "B";
  if (s >= 50) return "C";
  if (s >= 35) return "D";
  return "F";
}

function ScoreBar({ label, value, max = 100 }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="seo-score-bar-row">
      <span className="seo-score-bar-label">{label}</span>
      <div className="seo-score-bar-track">
        <div className="seo-score-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="seo-score-bar-val">{value}/{max}</span>
    </div>
  );
}

export default function SEOAudit({ onChangeAdded }) {
  const [audit, setAudit]           = useState(null);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState(null);  // product detail
  const [generating, setGenerating] = useState(null);  // product id being generated
  const [genResult, setGenResult]   = useState(null);
  const [filter, setFilter]         = useState("all"); // all / poor / good

  const loadAudit = async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/seo/audit/site");
      const d = await r.json();
      if (d.success) setAudit(d.audit);
    } catch (e) {
      console.error("Audit load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAudit(); }, []);

  const generateForProduct = async (productId) => {
    setGenerating(productId);
    setGenResult(null);
    try {
      const r = await fetch(`/api/seo/generate/product/${productId}`, { method: "POST" });
      const d = await r.json();
      setGenResult(d);
      if (d.success && onChangeAdded) onChangeAdded();
    } catch (e) {
      setGenResult({ error: "Request failed" });
    } finally {
      setGenerating(null);
    }
  };

  const generateBulk = async () => {
    setGenerating("bulk");
    setGenResult(null);
    try {
      const r = await fetch("/api/seo/generate/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      setGenResult(d);
      if (d.success && onChangeAdded) onChangeAdded();
    } catch (e) {
      setGenResult({ error: "Bulk generation failed" });
    } finally {
      setGenerating(null);
    }
  };

  const products = audit?.products || [];
  const summary  = audit?.summary  || {};

  const filtered = products.filter(p => {
    if (filter === "poor") return p.overall_score < 50;
    if (filter === "good") return p.overall_score >= 80;
    return true;
  });

  return (
    <div className="seo-audit-wrap">
      {/* Summary strip */}
      {summary.total_products && (
        <div className="seo-audit-summary-strip">
          <div className="seo-audit-summary-item">
            <span className="seo-audit-summary-num">{summary.total_products}</span>
            <span className="seo-audit-summary-lbl">Products</span>
          </div>
          <div className="seo-audit-summary-item">
            <span className="seo-audit-summary-num">{summary.average_score}</span>
            <span className="seo-audit-summary-lbl">Avg Score</span>
          </div>
          <div className="seo-audit-summary-item">
            <span className="seo-audit-summary-num seo-text-green">{summary.products_with_images_no_alt || 0}</span>
            <span className="seo-audit-summary-lbl">Missing Alt</span>
          </div>
          <div className="seo-audit-summary-item">
            <span className="seo-audit-summary-num seo-text-warn">{summary.products_short_description || 0}</span>
            <span className="seo-audit-summary-lbl">Short Desc</span>
          </div>
          <div className="seo-audit-summary-item">
            <span className="seo-audit-summary-num seo-text-red">{summary.grade_distribution?.F || 0}</span>
            <span className="seo-audit-summary-lbl">Grade F</span>
          </div>
        </div>
      )}

      {/* Actions bar */}
      <div className="seo-audit-actions">
        <div className="seo-filter-tabs">
          {["all", "poor", "good"].map(f => (
            <button
              key={f}
              className={`seo-filter-tab ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? "All Products" : f === "poor" ? "Needs Work (<50)" : "Good (80+)"}
            </button>
          ))}
        </div>
        <div className="seo-audit-btns">
          <button className="seo-btn seo-btn-ghost" onClick={loadAudit} disabled={loading}>
            {loading ? "⟳ Scanning…" : "↺ Refresh Audit"}
          </button>
          <button
            className="seo-btn seo-btn-primary"
            onClick={generateBulk}
            disabled={!!generating}
          >
            {generating === "bulk" ? "⟳ Generating…" : "⚡ Generate Top 10"}
          </button>
        </div>
      </div>

      {/* Gen result toast */}
      {genResult && (
        <div className={`seo-toast ${genResult.error ? "seo-toast-error" : "seo-toast-ok"}`}>
          {genResult.error
            ? `Error: ${genResult.error}`
            : genResult.generated !== undefined
              ? `Generated SEO for ${genResult.generated} products — ${genResult.products?.reduce((a, p) => a + (p.changes || 0), 0) || 0} changes queued`
              : "SEO generated — check Approval Queue"}
          <button className="seo-toast-close" onClick={() => setGenResult(null)}>✕</button>
        </div>
      )}

      {/* Product list */}
      <div className="seo-product-list">
        {loading && <div className="seo-loading">Scanning all products…</div>}
        {!loading && filtered.length === 0 && (
          <div className="seo-empty">No products match this filter.</div>
        )}
        {filtered.map(p => {
          const grade = gradeFromScore(p.overall_score);
          const isOpen = selected === p.product_id;
          return (
            <div key={p.product_id} className="seo-product-card">
              <div
                className="seo-product-card-header"
                onClick={() => setSelected(isOpen ? null : p.product_id)}
              >
                <div className="seo-product-card-left">
                  <span className={`seo-grade-badge ${GRADE_BADGE[grade] || ""}`}>{grade}</span>
                  <div>
                    <div className="seo-product-title">{p.title}</div>
                    <div className="seo-product-meta">{p.product_type} · {p.vendor}</div>
                  </div>
                </div>
                <div className="seo-product-card-right">
                  <ScoreGauge score={p.overall_score} size={56} />
                  <button
                    className="seo-btn seo-btn-sm seo-btn-primary"
                    onClick={e => { e.stopPropagation(); generateForProduct(p.product_id); }}
                    disabled={generating === p.product_id}
                  >
                    {generating === p.product_id ? "⟳" : "⚡ Generate"}
                  </button>
                  <span className="seo-product-chevron">{isOpen ? "▲" : "▼"}</span>
                </div>
              </div>

              {isOpen && (
                <div className="seo-product-card-detail">
                  <div className="seo-score-breakdown">
                    <ScoreBar label="Title"       value={p.breakdown?.title?.score       || 0} max={p.breakdown?.title?.max       || 25} />
                    <ScoreBar label="Body"        value={p.breakdown?.body?.score        || 0} max={p.breakdown?.body?.max        || 25} />
                    <ScoreBar label="Description" value={p.breakdown?.description?.score || 0} max={p.breakdown?.description?.max || 20} />
                    <ScoreBar label="Images"      value={p.breakdown?.images?.score      || 0} max={p.breakdown?.images?.max      || 15} />
                    <ScoreBar label="Handle"      value={p.breakdown?.handle?.score      || 0} max={p.breakdown?.handle?.max      || 10} />
                    <ScoreBar label="Tags"        value={p.breakdown?.tags?.score        || 0} max={p.breakdown?.tags?.max        ||  5} />
                  </div>
                  {p.issues?.length > 0 && (
                    <div className="seo-issues-list">
                      <div className="seo-issues-title">Issues</div>
                      {p.issues.map((issue, i) => (
                        <div key={i} className={`seo-issue seo-issue-${issue.severity}`}>
                          <span className="seo-issue-icon">
                            {issue.severity === "critical" ? "🔴" : issue.severity === "warning" ? "🟡" : "🔵"}
                          </span>
                          {issue.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
